import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Video,
  CalendarClock,
  Sparkles,
  Users,
  FolderOpen,
  ListTodo,
  Kanban,
  Settings,
  Menu,
  X,
  LogOut,
  ChevronDown,
  Plus,
  StickyNote,
  Search,
  Shield,
} from "lucide-react";
import BloqLogo from "@/components/BloqLogo";
import NotificationsBell from "@/components/NotificationsBell";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/app/search", label: "Search", icon: Search },
  { to: "/app/meetings", label: "Meetings", icon: Video },
  { to: "/app/bookings", label: "Bookings", icon: CalendarClock },
  { to: "/app/recall", label: "Recall", icon: Sparkles },
  { to: "/app/contacts", label: "Contacts", icon: Users },
  { to: "/app/tasks", label: "Tasks", icon: ListTodo },
  { to: "/app/notes", label: "Notes", icon: StickyNote },
  { to: "/app/files", label: "Files", icon: FolderOpen },
  // Roadmap is admin-only (`user.is_admin` from the JWT). Hidden from
  // every other user. This is the Bloq team's working roadmap; cards
  // toggled `is_public` show up on the marketing /roadmap page.
  { to: "/app/roadmap", label: "Roadmap", icon: Kanban, adminOnly: true },
  { to: "/app/settings/marketing", label: "Marketing", icon: Sparkles, adminOnly: true },
  // Destructive DB sweep — Bloq team only. Hidden from every other user
  // and protected by AdminRoute + the require_bloq_admin server gate.
  { to: "/app/admin/users", label: "Admin", icon: Shield, adminOnly: true },
  { to: "/app/settings", label: "Settings", icon: Settings },
];

function initials(name) {
  if (!name) return "B";
  return name
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { workspaces, currentWorkspace, switchWorkspace } = useWorkspace();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const submitSearch = (e) => {
    if (e) e.preventDefault();
    const q = (searchQ || "").trim();
    if (!q) {
      navigate("/app/search");
      return;
    }
    navigate(`/app/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="min-h-screen w-full" data-testid="app-shell">
      {/* Sidebar - desktop */}
      <aside
        className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 flex-col px-5 py-6 z-30"
        style={{
          background: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(24px)",
          boxShadow: "1px 0 0 rgba(26,26,31,0.04)",
        }}
        data-testid="sidebar-desktop"
      >
        <div className="mb-7 flex items-center justify-between">
          <BrandLogo workspace={currentWorkspace} fallbackSize={42} maxWidth={177} />
          <NotificationsBell />
        </div>

        {/* Workspace switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger
            data-testid="workspace-switcher"
            className="w-full flex items-center justify-between gap-2 px-3 py-2.5 mb-5 rounded-lg bloq-card-soft hover:bg-[#efe5d6] transition-colors"
          >
            <div className="text-left overflow-hidden">
              <p className="text-[10px] uppercase tracking-widest text-[#82828c] font-medium">
                Workspace
              </p>
              <p className="text-sm font-medium truncate">
                {currentWorkspace?.name || "Loading…"}
              </p>
            </div>
            <ChevronDown className="w-4 h-4 text-[#82828c]" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-60">
            <DropdownMenuLabel>Switch workspace</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {workspaces.map((w) => (
              <DropdownMenuItem
                key={w.workspace_id}
                data-testid={`workspace-option-${w.workspace_id}`}
                onClick={() => switchWorkspace(w.workspace_id)}
              >
                {w.name}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => navigate("/app/settings?tab=workspace")}
              data-testid="manage-workspaces-link"
            >
              <Plus className="w-4 h-4 mr-2" /> Manage workspaces
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Global search shortcut */}
        <form
          onSubmit={submitSearch}
          className="mb-4 relative"
          role="search"
          data-testid="sidebar-search-form"
        >
          <Search className="w-4 h-4 text-[#82828c] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Search Bloq…"
            className="bloq-input pl-9 text-sm w-full py-2"
            data-testid="sidebar-search-input"
          />
        </form>

        <nav className="flex-1 flex flex-col gap-0.5 overflow-y-auto">
          {NAV.filter((n) => !n.adminOnly || user?.is_admin).map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.exact}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `bloq-sidebar-link ${isActive ? "" : ""}`
                }
                style={({ isActive }) =>
                  isActive
                    ? {
                        background: "rgba(0, 94, 122, 0.08)",
                        color: "var(--bloq-primary)",
                        fontWeight: 600,
                      }
                    : {}
                }
              >
                <Icon className="w-[18px] h-[18px]" />
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        {/* User pill */}
        <DropdownMenu>
          <DropdownMenuTrigger
            data-testid="user-menu-trigger"
            className="mt-3 flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-[#f5efe6] transition-colors text-left w-full"
          >
            <Avatar className="w-9 h-9 rounded-md">
              <AvatarImage src={user?.avatar_url || ""} className="rounded-md" />
              <AvatarFallback className="bg-[#005e7a] text-white text-xs rounded-md">
                {initials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 overflow-hidden">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-[#82828c] truncate">{user?.email}</p>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={() => navigate("/app/settings?tab=profile")}>
              <Settings className="w-4 h-4 mr-2" /> Account settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-2" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </aside>

      {/* Mobile topbar */}
      <header
        className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-5 py-3"
        style={{
          background: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(20px)",
          boxShadow: "0 1px 0 rgba(26,26,31,0.05)",
        }}
      >
        <BrandLogo workspace={currentWorkspace} fallbackSize={28} />
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          className="w-10 h-10 rounded-lg flex items-center justify-center hover:bg-[#f5efe6]"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </header>

      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 top-[56px] z-30 px-5 pt-4 pb-6 overflow-y-auto"
          style={{ background: "rgba(255,255,255,0.98)", backdropFilter: "blur(20px)" }}
          data-testid="mobile-menu"
        >
          <DropdownMenu>
            <DropdownMenuTrigger className="w-full flex items-center justify-between gap-2 px-3 py-2.5 mb-4 rounded-lg bloq-card-soft">
              <span className="text-sm font-medium">{currentWorkspace?.name}</span>
              <ChevronDown className="w-4 h-4 text-[#82828c]" />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {workspaces.map((w) => (
                <DropdownMenuItem
                  key={w.workspace_id}
                  onClick={() => {
                    switchWorkspace(w.workspace_id);
                    setMobileOpen(false);
                  }}
                >
                  {w.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <nav className="flex flex-col gap-1">
            {NAV.filter((n) => !n.adminOnly || user?.is_admin).map((n) => {
              const Icon = n.icon;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.exact}
                  onClick={() => setMobileOpen(false)}
                  className="bloq-sidebar-link"
                  style={({ isActive }) =>
                    isActive
                      ? {
                          background: "rgba(0, 94, 122, 0.08)",
                          color: "var(--bloq-primary)",
                        }
                      : {}
                  }
                >
                  <Icon className="w-[18px] h-[18px]" />
                  {n.label}
                </NavLink>
              );
            })}
            <button
              onClick={handleLogout}
              className="bloq-sidebar-link text-left mt-2"
              data-testid="mobile-logout-button"
            >
              <LogOut className="w-[18px] h-[18px]" /> Log out
            </button>
          </nav>
        </div>
      )}

      <main className="lg:pl-64 min-h-screen">
        <VerifyEmailBanner />
        <div className="px-5 sm:px-8 lg:px-10 py-6 sm:py-10">{children}</div>
      </main>
    </div>
  );
}

/**
 * Renders the workspace's branded email logo when set (via the Branding
 * tab in Email Setup). Falls back to the Bloq logo when blank — same
 * behavior the email templates use, so the sidebar and emails always
 * match. Width is capped to keep the sidebar layout intact.
 */
function BrandLogo({ workspace, fallbackSize = 26, maxWidth = 230 }) {
  const url = (workspace?.email_logo_url || workspace?.logo_url || "").trim();
  if (!url) return <BloqLogo size={fallbackSize} />;
  return (
    <img
      src={url}
      alt={workspace?.name || "Workspace logo"}
      className="object-contain"
      style={{ height: `${fallbackSize}px`, maxWidth: `${maxWidth}px` }}
      data-testid="sidebar-workspace-logo"
    />
  );
}
